import React, { useState, useMemo } from 'react';

interface DataTableCardProps {
    data: Record<string, any>[];
    title?: string;
    rowsPerPage?: number;
}

const DataTableCard: React.FC<DataTableCardProps> = ({ 
    data, 
    title = 'Importierte Daten',
    rowsPerPage = 20 
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

    // Get all unique columns from all rows
    const allColumns = useMemo(() => {
        const columnsSet = new Set<string>();
        data.forEach(row => {
            Object.keys(row).forEach(key => columnsSet.add(key));
        });
        return Array.from(columnsSet);
    }, [data]);

    // Filter data based on column filters
    const filteredData = useMemo(() => {
        if (Object.keys(columnFilters).length === 0 || Object.values(columnFilters).every(v => !v)) {
            return data;
        }

        return data.filter(row => {
            return Object.entries(columnFilters).every(([column, filterValue]) => {
                if (!filterValue) return true;
                const cellValue = row[column];
                if (cellValue === null || cellValue === undefined) return false;
                return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
            });
        });
    }, [data, columnFilters]);

    // Paginate filtered data
    const paginatedData = useMemo(() => {
        return filteredData.slice(
            (currentPage - 1) * rowsPerPage,
            currentPage * rowsPerPage
        );
    }, [filteredData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleFilterChange = (column: string, value: string) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: value
        }));
        setCurrentPage(1); // Reset to first page when filtering
    };

    const clearFilters = () => {
        setColumnFilters({});
        setCurrentPage(1);
    };

    const hasActiveFilters = Object.values(columnFilters).some(v => v);

    if (data.length === 0) {
        return (
            <div className="data-table-card">
                <div className="table-header">
                    <h3>{title}</h3>
                </div>
                <div className="content-empty">
                    <p>Keine Daten gefunden</p>
                </div>
            </div>
        );
    }

    return (
        <div className="data-table-card">
            <div className="table-header">
                <h3>{title}</h3>
                <div className="table-header-actions">
                    {hasActiveFilters && (
                        <button 
                            className="clear-filters-btn"
                            onClick={clearFilters}
                            title="Filter zurücksetzen"
                        >
                            Filter zurücksetzen
                        </button>
                    )}
                    <div className="table-info">
                        Zeige {filteredData.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} - {Math.min(currentPage * rowsPerPage, filteredData.length)} von {filteredData.length} Einträgen
                        {filteredData.length !== data.length && ` (gefiltert von ${data.length})`}
                    </div>
                </div>
            </div>
            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            {allColumns.map((column, idx) => (
                                <th key={idx}>
                                    <div className="table-header-cell">
                                        <span className="column-name">{column}</span>
                                        <input
                                            type="text"
                                            className="column-filter"
                                            placeholder="Filter..."
                                            value={columnFilters[column] || ''}
                                            onChange={(e) => handleFilterChange(column, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                                {allColumns.map((column, cellIdx) => (
                                    <td key={cellIdx}>
                                        {row[column] !== undefined && row[column] !== null 
                                            ? String(row[column]) 
                                            : ''}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Zurück
                    </button>
                    <span className="pagination-info">
                        Seite {currentPage} von {totalPages}
                    </span>
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Weiter
                    </button>
                </div>
            )}
        </div>
    );
};

export default DataTableCard;
